import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

/**
 * WorkAndGoalsEformTab
 *
 * Handles Work & Goals section for Network Eform reports
 * Maps to NPDetails.eForm table fields
 *
 * Report types: 2010 (Network Eform Initial), 2011 (Network Eform Follow Up)
 * Section: psych-eform-work-and-goals
 */

const WorkAndGoalsEformTab = ({ reportId, api, onSave , isPublished}) => {
  // Determine report type
  const [reportType, setReportType] = useState(null);

  // Form state - common fields
  const [workStatus, setWorkStatus] = useState('');
  const [workDuties, setWorkDuties] = useState('');
  const [workRecommendations, setWorkRecommendations] = useState('');
  const [tg1, setTG1] = useState('');
  const [tg2, setTG2] = useState('');
  const [motivationToChange, setMotivationToChange] = useState('');

  // Form state - Initial specific fields
  const [workRelatedAtAssessment, setWorkRelatedAtAssessment] = useState('');

  // Form state - Follow Up specific fields
  const [assessment1, setAssessment1] = useState('');
  const [tg1Achieved, setTG1Achieved] = useState('');
  const [assessment2, setAssessment2] = useState('');
  const [tg2Achieved, setTG2Achieved] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  // Dropdown options with defaults
  const [workRelatedOptions, setWorkRelatedOptions] = useState([
    { value: 'Not Work Related', label: 'Not Work Related' },
    { value: 'Work Related', label: 'Work Related' },
    { value: 'Exasperated By Work', label: 'Exasperated By Work' }
  ]);
  const [workStatusOptions, setWorkStatusOptions] = useState([
    { value: '1', label: 'Full duties' },
    { value: '2', label: 'Amended duties' },
    { value: '4', label: 'Off work' }
  ]);

  const isInitial = reportType === 2010;
  const isFollowUp = reportType === 2011;

  useEffect(() => {
    loadWorkAndGoalsData();
  }, [reportId]);

  const loadWorkAndGoalsData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-eform-work-and-goals`);

      if (response.ok) {
        const data = await response.json();

        // Determine report type
        setReportType(data.reportTypeId || 2010);

        // Common fields
        setWorkStatus(data.workStatus || '');
        setWorkDuties(data.workDuties || '');
        setWorkRecommendations(data.workRecommendations || '');
        setTG1(data.tg1 || '');
        setTG2(data.tg2 || '');
        setMotivationToChange(data.motivationToChange || '');

        // Initial specific fields
        if (data.reportTypeId === 2010) {
          setWorkRelatedAtAssessment(data.workRelatedAtAssessment || '');
        }

        // Follow Up specific fields
        if (data.reportTypeId === 2011) {
          setAssessment1(data.assessment1 || '');
          setTG1Achieved(data.tg1Achieved !== null ? data.tg1Achieved : '');
          setAssessment2(data.assessment2 || '');
          setTG2Achieved(data.tg2Achieved !== null ? data.tg2Achieved : '');
        }

        // Load dropdown options
        if (data.workRelatedOptions) setWorkRelatedOptions(data.workRelatedOptions);
        if (data.workStatusOptions) setWorkStatusOptions(data.workStatusOptions);

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.workStatus) initialSavingStates['WorkStatus'] = 'saved';
        if (data.workDuties) initialSavingStates['WorkDuties'] = 'saved';
        if (data.workRecommendations) initialSavingStates['WorkRecommendations'] = 'saved';
        if (data.tg1) initialSavingStates['TG1'] = 'saved';
        if (data.tg2) initialSavingStates['TG2'] = 'saved';
        if (data.motivationToChange) initialSavingStates['MotivationToChange'] = 'saved';

        if (data.reportTypeId === 2010) {
          if (data.workRelatedAtAssessment) initialSavingStates['WorkRelatedAtAssessment'] = 'saved';
        }

        if (data.reportTypeId === 2011) {
          if (data.assessment1) initialSavingStates['Assessment1'] = 'saved';
          if (data.tg1Achieved !== null) initialSavingStates['TG1Achieved'] = 'saved';
          if (data.assessment2) initialSavingStates['Assessment2'] = 'saved';
          if (data.tg2Achieved !== null) initialSavingStates['TG2Achieved'] = 'saved';
        }
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading work and goals data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-eform-work-and-goals/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'WorkRelatedAtAssessment': workRelatedAtAssessment,
      'WorkStatus': workStatus,
      'WorkDuties': workDuties,
      'WorkRecommendations': workRecommendations,
      'TG1': tg1,
      'TG2': tg2,
      'MotivationToChange': motivationToChange,
      'Assessment1': assessment1,
      'TG1Achieved': tg1Achieved,
      'Assessment2': assessment2,
      'TG2Achieved': tg2Achieved
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleSelectChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleNumberChange = (fieldName, value, setter) => {
    setter(value);
    const numValue = value === '' ? null : parseInt(value, 10);
    saveField(fieldName, numValue);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading work and goals...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-section">
        <h3>Work & Goals</h3>

        {isInitial && (
          <div className="form-group">
            <label htmlFor="workRelatedAtAssessment">
              Work Related: <span style={{ color: 'red' }}>*</span>
              {getSaveIndicator('WorkRelatedAtAssessment')}
            </label>
            <select
              id="workRelatedAtAssessment"
              value={workRelatedAtAssessment}
              onChange={(e) => handleSelectChange('WorkRelatedAtAssessment', e.target.value, setWorkRelatedAtAssessment)}
              style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
            >
              <option value="">Select</option>
              {workRelatedOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="workStatus">
            What is the patient's work status? <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('WorkStatus')}
          </label>
          <select
            id="workStatus"
            value={workStatus}
            onChange={(e) => handleSelectChange('WorkStatus', e.target.value, setWorkStatus)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option value="">Select</option>
            {workStatusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="workDuties">
            Work duties: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('WorkDuties')}
          </label>
          <textarea
            id="workDuties"
            value={workDuties}
            onChange={(e) => handleFieldChange('WorkDuties', e.target.value, setWorkDuties)}
            onBlur={() => handleFieldBlur('WorkDuties', workDuties)}
            rows="4"
            maxLength="50"
            style={{ width: '100%' }}
          />
          <span className="character-counter">
            {workDuties.length} / 50 characters
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="workRecommendations">
            Work {isInitial ? 'Recommendations' : 'recommendations'}: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('WorkRecommendations')}
          </label>
          <textarea
            id="workRecommendations"
            value={workRecommendations}
            onChange={(e) => handleFieldChange('WorkRecommendations', e.target.value, setWorkRecommendations)}
            onBlur={() => handleFieldBlur('WorkRecommendations', workRecommendations)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        {isFollowUp && (
          <div className="form-group">
            <label htmlFor="assessment1">
              Goal 1 previous: <span style={{ color: 'red' }}>*</span>
              {getSaveIndicator('Assessment1')}
            </label>
            <textarea
              id="assessment1"
              value={assessment1}
              disabled
              rows="3"
              style={{ width: '100%', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="tg1">
            {isFollowUp ? 'Goal 1 Update' : 'Goal 1'}: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('TG1')}
          </label>
          <textarea
            id="tg1"
            value={tg1}
            onChange={(e) => handleFieldChange('TG1', e.target.value, setTG1)}
            onBlur={() => handleFieldBlur('TG1', tg1)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>

        {isFollowUp && (
          <div className="form-group">
            <label htmlFor="tg1Achieved">
              % Achieved: <span style={{ color: 'red' }}>*</span>
              {getSaveIndicator('TG1Achieved')}
            </label>
            <input
              type="number"
              id="tg1Achieved"
              value={tg1Achieved}
              onChange={(e) => handleNumberChange('TG1Achieved', e.target.value, setTG1Achieved)}
              min="0"
              max="100"
              style={{ width: '150px', padding: '8px' }}
            />
            <span style={{ marginLeft: '8px' }}>%</span>
          </div>
        )}

        {isFollowUp && (
          <div className="form-group">
            <label htmlFor="assessment2">
              Goal 2 previous: <span style={{ color: 'red' }}>*</span>
              {getSaveIndicator('Assessment2')}
            </label>
            <textarea
              id="assessment2"
              value={assessment2}
              disabled
              rows="3"
              style={{ width: '100%', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="tg2">
            {isFollowUp ? 'Goal 2 Update' : 'Goal 2'}: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('TG2')}
          </label>
          <textarea
            id="tg2"
            value={tg2}
            onChange={(e) => handleFieldChange('TG2', e.target.value, setTG2)}
            onBlur={() => handleFieldBlur('TG2', tg2)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>

        {isFollowUp && (
          <div className="form-group">
            <label htmlFor="tg2Achieved">
              % Achieved: <span style={{ color: 'red' }}>*</span>
              {getSaveIndicator('TG2Achieved')}
            </label>
            <input
              type="number"
              id="tg2Achieved"
              value={tg2Achieved}
              onChange={(e) => handleNumberChange('TG2Achieved', e.target.value, setTG2Achieved)}
              min="0"
              max="100"
              style={{ width: '150px', padding: '8px' }}
            />
            <span style={{ marginLeft: '8px' }}>%</span>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="motivationToChange">
            Motivation to change including any identified barriers: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('MotivationToChange')}
          </label>
          <textarea
            id="motivationToChange"
            value={motivationToChange}
            onChange={(e) => handleFieldChange('MotivationToChange', e.target.value, setMotivationToChange)}
            onBlur={() => handleFieldBlur('MotivationToChange', motivationToChange)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default WorkAndGoalsEformTab;
